import React, { FC, useState } from 'react';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import {
  makeStyles,
  Theme,
  createStyles,
  Grid,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Radio,
} from '@material-ui/core';
import { useTranslation } from '../../../../hooks/useTranslation';
import colors from '../../../../themes/main-colors';
import Button from '@material-ui/core/Button';
import {
  OutageLookupParams,
  OutageLookupResponse,
  OutageLookUpType,
  PremiseInfo,
  ServicePointDetail,
} from '../../../../__generated__/pge-types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formItem: {
      marginTop: '1rem',
      paddingLeft: 0,
    },
    bodyText: {
      lineHeight: '24px',
      fontSize: '1.25rem',
      paddingTop: 10,
    },
    bodyTextSmall: {
      lineHeight: '20px',
      fontSize: '1rem',
      paddingLeft: 24,
    },
    radioButtonIcon: {
      '& .MuiSvgIcon-root': {
        fontSize: '1rem',
      },
    },
    submitButtonWrapper: {
      float: 'right',
      [theme.breakpoints.down('xs')]: {
        margin: theme.spacing(2, 0, 1, 0),
        textAlign: 'center',
        float: 'none',
      },
    },
    submitButton: {
      padding: theme.spacing(1.625),
      backgroundColor: colors.orange,
      borderRadius: 5,
      color: colors.white,
      fontSize: '1.125rem',
      lineHeight: '1.125rem',
      width: '100%',
      '&:active': {
        backgroundColor: colors.orange,
      },
      '&:hover': {
        backgroundColor: colors.orange,
      },
      '&.MuiButton-root.Mui-disabled': {
        color: colors.quickSilverGrey,
        backgroundColor: colors.disableColor,
      },
    },
    heading: {
      fontSize: '2.25rem',
      lineHeight: '2.25rem',
      color: colors.noirBlur,
    },
    multiplePremiseWrapper: {
      background: colors.white,
      padding: '20px 24px 20px 0px',
      marginTop: 25,
    },
    backButton: {
      fontSize: '1.125rem',
      lineHeight: '1.375rem',
      padding: theme.spacing(1.125, 1.875),
      float: 'left',
      cursor: 'pointer',
      textDecoration: 'underline',
      '&:hover': {
        textDecoration: 'underline',
      },
      [theme.breakpoints.down('xs')]: {
        float: 'none',
        textAlign: 'center',
      },
    },
    buttonSection: {
      marginTop: 20,
    },
    cancelButton: {
      color: colors.brightBlue,
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
  }),
);

type Props = {
  open: boolean;
  onClose: () => void;
  outageDetailData: OutageLookupResponse;
  selectedPremiseParams: (
    data: OutageLookupParams,
    houseNumber: string | undefined,
    premise: PremiseInfo | null,
  ) => void;
};

const MultiplePremiseWebView: FC<Props> = ({
  open,
  onClose,
  outageDetailData,
  selectedPremiseParams,
}: Props) => {
  const classes = useStyles();
  const [premiseChecked, setPremiseChecked] = useState<boolean>(false);
  const [premiseHouseNumber, setPremiseHouseNumber] = useState<
    string | undefined
  >();
  const [premiseEncryptedValue, setPremiseEncryptedValue] = useState<string>(
    '',
  );
  const [selectedPremise, setSelectedPremise] = useState<PremiseInfo | null>(
    null,
  );

  const { t } = useTranslation();

  const isMultipleAddressExist =
    outageDetailData?.multiplePremiseList !== null &&
    outageDetailData?.multiplePremiseList !== undefined &&
    outageDetailData?.multiplePremiseList?.length > 1
      ? true
      : false;
  const onHouseNumberChecked = (e: any) => {
    const selectedVal = JSON.parse(e.target.value);
    setPremiseEncryptedValue(selectedVal.encryptedValue);
    setPremiseChecked(true);
    setPremiseHouseNumber(selectedVal.houseNumber);
    setSelectedPremise(selectedVal.premise);
  };
  const handleOnSubmit = () => {
    if (premiseChecked && premiseEncryptedValue) {
      const params: OutageLookupParams = {
        lookupType: OutageLookUpType.Premise,
        encryptedValue: premiseEncryptedValue,
      };
      selectedPremiseParams(params, premiseHouseNumber, selectedPremise);
    }
  };

  const phoneNumber = t('CUSTOMER_CARE_NUMBER_OTHER');

  return (
    <Box component="div">
      <Grid item>
        <Typography variant={'h2'} className={classes.heading}>
          {t('SELECT_SERVICE_ADDRESS')}
        </Typography>
      </Grid>
      <Grid item>
        <Typography variant={'body1'} className={classes.bodyText}>
          {t('MULTIPLE_SERVICE_ADDRESS_TEXT')}
        </Typography>
      </Grid>
      <Grid className={classes.multiplePremiseWrapper}>
        <Typography variant={'body1'} className={classes.bodyTextSmall}>
          {t('PGE_CUSTOMER_CALL_IF_NOT_ABLE_IDENTIFY_OUTAGE_LOCATION')}{' '}
          {
            <a
              href={'tel:' + phoneNumber}
              style={{ textDecoration: 'none', color: colors.brightBlue }}
            >
              {' '}
              <strong>{phoneNumber}</strong>
            </a>
          }
        </Typography>
        <Grid item className={classes.formItem}>
          {isMultipleAddressExist && (
            <Box flexDirection={'row'}>
              <FormControl component="fieldset">
                <RadioGroup
                  aria-label={'report-outage-multiple-addresses'}
                  name={'selectAddressForOutage'}
                  onChange={e => onHouseNumberChecked(e)}
                >
                  <Grid
                    container
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      paddingLeft: 20,
                    }}
                    direction={'column'}
                  >
                    {outageDetailData?.multiplePremiseList?.map(
                      (account, idx: number) => {
                        // @ts-ignore
                        const premiseInfo = account?.premiseInfo;
                        return (
                          <FormControlLabel
                            value={JSON.stringify({
                              encryptedValue: account?.encryptedValue,
                              houseNumber: account?.houseNumber,
                              premise: premiseInfo,
                            })}
                            control={
                              <Radio
                                color={'primary'}
                                className={classes.radioButtonIcon}
                              />
                            }
                            label={
                              <Box
                                css={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                }}
                              >
                                <Typography component="span">
                                  {account?.houseNumber +
                                    ' ' +
                                    account?.last4CharAddress}
                                </Typography>
                                {premiseInfo?.servicePointDetails?.find(
                                  (servicePoint: ServicePointDetail) =>
                                    servicePoint?.outageInfo?.hasKnownOutage,
                                ) && (
                                  <Typography
                                    component="span"
                                    css={{
                                      color: colors.errorRed,
                                      marginLeft: '12px',
                                      fontSize: '0.8rem',
                                      lineHeight: '0.8rem',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    (Outage Reported)
                                  </Typography>
                                )}
                              </Box>
                            }
                            data-testid={`house-number-item-${idx}`}
                            key={`house-number-item-${idx}`}
                          />
                        );
                      },
                    )}
                  </Grid>
                </RadioGroup>
              </FormControl>
            </Box>
          )}
        </Grid>
      </Grid>
      <Grid xs={12} sm={12} className={classes.buttonSection}>
        <Grid className={classes.backButton} xs={12} sm={6}>
          <Button
            className={classes.cancelButton}
            onClick={() => onClose()}
            disableRipple={true}
          >
            <Typography variant={'h4'} noWrap>
              {t('CANCEL')}
            </Typography>
          </Button>
        </Grid>
        <Grid xs={12} sm={6} className={classes.submitButtonWrapper}>
          <Button
            disabled={premiseChecked && premiseEncryptedValue ? false : true}
            size={'large'}
            className={classes.submitButton}
            onClick={async () => {
              await handleOnSubmit();
            }}
          >
            <Typography variant={'h4'}>{t('SHOW_DETAILS')}</Typography>
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MultiplePremiseWebView;
